import styled from '@emotion/styled';
import { motion } from 'framer-motion';
import { StaticImage } from 'gatsby-plugin-image';
import * as React from 'react';
import { useState } from 'react';
import {
  getMatchDetail,
  getMatches,
  getPlayerPhotos,
  getPlayerStandings,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  Match,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  MatchDetail,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  PlayerPhoto,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  PlayerStanding,
} from '../api/leaderboard';
import Leaderboard from '../components/leaderboard';
import MatchReplay from '../components/match-replay';
import SEO from '../components/seo';
import ExtendedBg from '../components/utility/extended-bg';
import ResponsiveDiv from '../components/utility/responsive-div';
import { Section, SectionContent, CenteredContent } from '../components/utility/section';
import VimeoVideo from '../components/vimeo-video';
import { useAsyncEffect } from '../hooks';
import { defaultTransition } from '../styles/animation';
import officeTourPreviewVideoUrl from '../videos/office-tour-preview.mp4';
import { PaddedDiv } from '../components/utility/padded-div';

const AboutPage = () => {
  const [playerStandings, setPlayerStandings] = useState<PlayerStanding[]>([]);
  const [playerPhotos, setPlayerPhotos] = useState<PlayerPhoto[]>([]);
  const [matches, setMatches] = useState<Match[]>([]);
  const [currentMatch, setCurrentMatch] = useState<MatchDetail>();
  const [currentMatchVideoUrl, setCurrentMatchVideoUrl] = useState<string>();

  const loadNextMatch = async () => {
    if (matches.length === 0) {
      return;
    }

    const currentMatchIndex = matches.findIndex((match) => match.id === currentMatch?.id);

    let nextMatch: Match;
    let nextMatchDetail: MatchDetail | undefined;

    // Some match IDs don't return valid match details
    // Continually loop through matches until a valid one is found
    for (let index = currentMatchIndex; !nextMatchDetail; index += 1) {
      const nextMatchIndex = (index + 1) % matches.length;
      nextMatch = matches[nextMatchIndex];
      nextMatchDetail = await getMatchDetail(nextMatch.id);
    }

    setCurrentMatch(nextMatchDetail);
    setCurrentMatchVideoUrl(nextMatch!.videoUrl);
  };

  useAsyncEffect(async () => {
    setPlayerStandings(await getPlayerStandings());
    setMatches(await getMatches());
    setPlayerPhotos(await getPlayerPhotos());
  }, []);

  useAsyncEffect(() => loadNextMatch(), [matches]);

  return (
    <>
      <SEO title="About" />
      <Section>
        <SectionContent>
          <AboutHeading
            animate={{ x: 0, opacity: 1 }}
            initial={{ x: 50, opacity: 0 }}
            transition={defaultTransition}
          >
            We know how to give our best.
            <motion.span
              animate={{ x: 0, opacity: 1 }}
              initial={{ x: 50, opacity: 0 }}
              transition={{
                ...defaultTransition,
                delay: 0.5,
              }}
            >
              We also know how to have fun.
            </motion.span>
          </AboutHeading>
        </SectionContent>
      </Section>
      <Section>
        <SectionContent>
          <VimeoVideo
            title="Launchcode Office Tour"
            vimeoVideoId="530483549"
            previewSrc={officeTourPreviewVideoUrl}
          />
        </SectionContent>
      </Section>
      <Section>
        <SectionContent>
          <CultureBlock>
            <h2>Launchcode culture</h2>
            <p>
              <b>Culture is King</b> and something we will not compromise on. This is one of our key
              differentiators, the thing that makes us great and enables a trusting work environment
              where people genuinely love to come to work each day. It's funny when we explain our
              culture to prospective teammates, we feel a lot of them think we over exaggerate how
              good really is. That is until they get here to experience it first hand and realize
              it's better than described. We'd love to share the experience with you, come be a part
              of our special project Fridays, hackathons, tech meetups or a game of squash (Did we
              mention we have our own squash court).
            </p>
          </CultureBlock>
          <ValuesPhotoWrapper>
            <StaticImage
              src="../images/values.png"
              alt="Launchcode values"
              loading="lazy"
              layout="fullWidth"
              placeholder="dominantColor"
            />
          </ValuesPhotoWrapper>
        </SectionContent>
      </Section>

      <ExtendedBg backgroundColor="#f7f7f9">
        <CenteredContent>
          <PaddedDiv>
            <MeetOurTeamSectionContent>
              <MeetOurTeamBlock>
                <h3>Meet our team</h3>
                <p>
                  What's the best way to show all of the launchers & coders on our team? By showing
                  them through a cool internal project they built. This is our squash ladder with a
                  tech twist. Our players keep track of scores and can save awesome instant replays
                  by the press of a button. The leaderboard is automatically calculated using
                  international elo ranking with points redistribution for those that play the most.
                  Best of all, an internal dashboard showing live rankings with all the replay
                  videos is projected on a big screen beside our kitchen so everyone can see.
                </p>
              </MeetOurTeamBlock>
            </MeetOurTeamSectionContent>
          </PaddedDiv>
        </CenteredContent>
        <LeaderboardContainer>
          <PaddedDiv>
            <ResponsiveDiv>
              <Leaderboard playerStandings={playerStandings} playerPhotos={playerPhotos} />
              <JoinUsForAGameWrapper>
                <h3>Join us for a game! </h3>
                <p>We hope you enjoy our live squash ladder as much as we do.</p>
                <p>
                  If you're interested in seeing how it works in person, we'd love to host you for a
                  squash game. However, since the scores are shown on our website, we can't
                  guarantee we will take it easy on you 😉
                </p>
              </JoinUsForAGameWrapper>
            </ResponsiveDiv>
          </PaddedDiv>
        </LeaderboardContainer>
        {currentMatch && (
          <MatchReplayContainer>
            <PaddedDiv>
              <ResponsiveDiv>
                <h3>Plays of the month</h3>
                <MatchReplay
                  match={currentMatch}
                  player1PhotoUrl={
                    playerPhotos.find((photo) => photo.id === currentMatch.player1.id)?.url
                  }
                  player2PhotoUrl={
                    playerPhotos.find((photo) => photo.id === currentMatch.player2.id)?.url
                  }
                  videoUrl={currentMatchVideoUrl!}
                  onVideoEnd={loadNextMatch}
                />
              </ResponsiveDiv>
            </PaddedDiv>
          </MatchReplayContainer>
        )}
      </ExtendedBg>
    </>
  );
};

const AboutHeading = styled(motion.h1)`
  display: flex;
  flex-direction: column;

  > span {
    margin-top: 1.45rem;
    color: inherit;
    font-weight: 900;
    text-rendering: optimizeLegibility;
    font-size: 2.5rem;
    line-height: 1.1;

    @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
      font-size: 4.5rem;
    }
  }
`;

const CultureBlock = styled.div`
  max-width: 105ch;
`;

const ValuesPhotoWrapper = styled.div`
  margin-top: 7.5rem;
  width: 100%;

  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    margin-bottom: 3.5rem;
  }
`;

const MeetOurTeamSectionContent = styled(ResponsiveDiv)`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  padding: 6.25rem 0;
`;

const MeetOurTeamBlock = styled.div`
  text-align: center;
  max-width: 90ch;
`;

const LeaderboardContainer = styled(CenteredContent)`
  width: 100%;

  > div {
    width: 100%;

    > div {
      width: 100%;
    }
  }
`;

const JoinUsForAGameWrapper = styled.div`
  width: 100%;

  > p {
    max-width: 80ch;
  }
`;

const MatchReplayContainer = styled(LeaderboardContainer)`
  margin-top: 3rem;
  margin-bottom: 3rem;

  @media (min-width: ${({ theme }) => theme.breakpoints.xs}) {
    margin-top: 2rem;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.sm}) {
    margin: 4rem 0;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    margin-bottom: 8rem;
  }
`;

export default AboutPage;
